import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { API_URL } from "../lib/utils";
import axios from "axios";
import { toast } from "sonner";
import { Boxes, Plus, Trash2, MinusCircle } from "lucide-react";

const lotStatus = (lot) => {
  if (!lot.expiry_date) return { label: "Sem validade", cls: "bg-slate-500/20 text-slate-500" };
  const today = new Date().toISOString().slice(0, 10);
  const soon = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10);
  if (lot.expiry_date < today) return { label: "EXPIRADO", cls: "bg-red-500/20 text-red-500" };
  if (lot.expiry_date <= soon) return { label: "A expirar", cls: "bg-amber-500/20 text-amber-600" };
  return { label: "OK", cls: "bg-emerald-500/20 text-emerald-600" };
};

export default function LotsPage() {
  const { token } = useAuth();
  const [lots, setLots] = useState([]);
  const [products, setProducts] = useState([]);
  const [modal, setModal] = useState(false);
  const [consumeModal, setConsumeModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [consumeQty, setConsumeQty] = useState("");
  const [form, setForm] = useState({ product_id: "", lot_number: "", quantity: "", expiry_date: "" });

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchAll = async () => {
    try {
      const [l, p] = await Promise.all([
        axios.get(`${API_URL}/lots`, { headers, withCredentials: true }),
        axios.get(`${API_URL}/products`, { headers, withCredentials: true }),
      ]);
      setLots(l.data); setProducts(p.data);
    } catch { toast.error("Erro ao carregar lotes"); }
  };

  const createLot = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/lots`, {
        ...form,
        quantity: parseFloat(form.quantity),
        expiry_date: form.expiry_date || null,
      }, { headers, withCredentials: true });
      toast.success("Lote criado");
      setModal(false); setForm({ product_id: "", lot_number: "", quantity: "", expiry_date: "" });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const consume = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/lots/${selected.lot_id}/consume?quantity=${parseFloat(consumeQty)}`, {}, { headers, withCredentials: true });
      toast.success("Consumo registado");
      setConsumeModal(false); setConsumeQty("");
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
  };

  const deleteLot = async (lot) => {
    if (!window.confirm(`Remover lote "${lot.lot_number}"?`)) return;
    await axios.delete(`${API_URL}/lots/${lot.lot_id}`, { headers, withCredentials: true });
    fetchAll();
  };

  const expiring = lots.filter((l) => lotStatus(l).label !== "OK" && lotStatus(l).label !== "Sem validade" && l.quantity > 0);

  return (
    <div className="space-y-6" data-testid="lots-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit']">Lotes & Validades</h1>
          <p className="text-muted-foreground">Rastreabilidade de lotes com controlo de validade</p>
        </div>
        <Button onClick={() => setModal(true)} data-testid="add-lot-btn"><Plus className="w-4 h-4 mr-2" /> Novo Lote</Button>
      </div>

      {expiring.length > 0 && (
        <Card className="border-amber-500/50 bg-amber-500/5">
          <CardContent className="p-4">
            <p className="font-semibold text-amber-600 text-sm">⚠ {expiring.length} lote(s) expirado(s) ou a expirar nos próximos 30 dias</p>
          </CardContent>
        </Card>
      )}

      {lots.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-muted-foreground"><Boxes className="w-10 h-10 mx-auto mb-3 opacity-40" />Nenhum lote registado</CardContent></Card>
      ) : (
        <Card><CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3">Produto</th>
                <th className="text-left p-3">Lote</th>
                <th className="text-right p-3">Quantidade</th>
                <th className="text-left p-3">Validade</th>
                <th className="text-left p-3">Estado</th>
                <th className="text-right p-3">Ações</th>
              </tr></thead>
              <tbody>
                {lots.map((l) => {
                  const st = lotStatus(l);
                  return (
                    <tr key={l.lot_id} className="border-b hover:bg-muted/30" data-testid={`lot-row-${l.lot_id}`}>
                      <td className="p-3 font-medium">{l.product_name}</td>
                      <td className="p-3 font-mono">{l.lot_number}</td>
                      <td className="p-3 text-right font-mono">{l.quantity} / {l.initial_quantity}</td>
                      <td className="p-3">{l.expiry_date || "-"}</td>
                      <td className="p-3"><Badge className={st.cls}>{st.label}</Badge></td>
                      <td className="p-3 text-right">
                        <div className="flex justify-end gap-1">
                          <Button size="sm" variant="outline" onClick={() => { setSelected(l); setConsumeModal(true); }} data-testid={`consume-lot-${l.lot_id}`}>
                            <MinusCircle className="w-4 h-4 mr-1" /> Consumir
                          </Button>
                          <Button size="sm" variant="ghost" className="text-destructive" onClick={() => deleteLot(l)}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent></Card>
      )}

      <Dialog open={modal} onOpenChange={setModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Novo Lote</DialogTitle></DialogHeader>
          <form onSubmit={createLot} className="space-y-3">
            <div className="space-y-1">
              <Label>Produto *</Label>
              <Select value={form.product_id} onValueChange={(v) => setForm({ ...form, product_id: v })}>
                <SelectTrigger data-testid="lot-product-select"><SelectValue placeholder="Escolher produto" /></SelectTrigger>
                <SelectContent>{products.map((p) => <SelectItem key={p.product_id} value={p.product_id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1"><Label>Nº do Lote *</Label><Input required value={form.lot_number} onChange={(e) => setForm({ ...form, lot_number: e.target.value })} data-testid="lot-number-input" /></div>
            <div className="space-y-1"><Label>Quantidade *</Label><Input required type="number" step="0.01" min="0.01" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} data-testid="lot-quantity-input" /></div>
            <div className="space-y-1"><Label>Validade</Label><Input type="date" value={form.expiry_date} onChange={(e) => setForm({ ...form, expiry_date: e.target.value })} data-testid="lot-expiry-input" /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setModal(false)}>Cancelar</Button>
              <Button type="submit" disabled={!form.product_id} data-testid="save-lot-btn">Criar</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={consumeModal} onOpenChange={setConsumeModal}>
        <DialogContent className="max-w-xs">
          <DialogHeader><DialogTitle>Consumir — {selected?.lot_number}</DialogTitle></DialogHeader>
          <form onSubmit={consume} className="space-y-3">
            <p className="text-sm text-muted-foreground">Disponível: {selected?.quantity}</p>
            <Input required type="number" step="0.01" min="0.01" max={selected?.quantity} value={consumeQty} onChange={(e) => setConsumeQty(e.target.value)} data-testid="consume-qty-input" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setConsumeModal(false)}>Cancelar</Button>
              <Button type="submit" data-testid="confirm-consume-btn">Consumir</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
